// 结果页面专用错误边界 - 针对评估结果显示的特殊错误处理
import React, { ReactNode } from 'react';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';

interface ResultsPageErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error) => void;
}

const ResultsPageErrorFallback: React.FC<{ onReset: () => void }> = ({ onReset }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
        {/* 错误图标 */}
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-10 h-10 text-orange-600" />
        </div>
        
        {/* 错误标题 */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          评估结果显示异常
        </h2>
        
        {/* 错误描述 */}
        <p className="text-gray-600 mb-8 leading-relaxed">
          很抱歉，评估结果页面在加载时遇到了技术问题。这可能是由于数据处理错误导致的。
          请尝试重新进行评估或联系技术支持。
        </p>
        
        {/* 操作建议 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
          <h3 className="font-semibold text-blue-800 mb-2">建议的解决方案:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 重新进行ICOPE健康评估</li>
            <li>• 检查网络连接是否正常</li>
            <li>• 清除浏览器缓存后重试</li>
            <li>• 如果问题持续存在，请联系技术支持</li>
          </ul>
        </div>
        
        {/* 操作按钮 */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/assessment-choice')}
            className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
          >
            <RefreshCw className="w-5 h-5" />
            <span>重新评估</span>
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onReset}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              重试显示
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回首页</span>
            </button>
          </div>
        </div>
        
        {/* 技术支持信息 */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            如需技术支持，请记住错误时间: {new Date().toLocaleString('zh-CN')}
          </p>
        </div>
      </div>
    </div>
  );
};

const ResultsPageErrorBoundary: React.FC<ResultsPageErrorBoundaryProps> = ({ 
  children, 
  onError 
}) => {
  const handleError = (error: Error) => {
    // 记录结果页面特有的错误信息
    console.error('🚨 评估结果页面错误:', {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // 调用外部错误处理函数
    if (onError) {
      onError(error);
    }
  };

  return (
    <ErrorBoundary
      onError={handleError}
      fallbackComponent={<ResultsPageErrorFallback onReset={() => window.location.reload()} />}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ResultsPageErrorBoundary;